import { Box, Circle, Icon } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Activity } from 'lucide-react'

import { HEALTH_COLOR } from './statusHealth'
import type { HealthLevel } from './useDiagnosticsSnapshot'

// Soft expanding ring, used to draw the eye when there's a problem. Pure CSS
// so it costs nothing in JS and pauses cleanly when unmounted.
const pingKeyframes = keyframes`
  0%   { transform: scale(1); opacity: 0.6; }
  70%  { transform: scale(2.4); opacity: 0; }
  100% { transform: scale(2.4); opacity: 0; }
`

type Props = {
  level: HealthLevel
  /** Pulse the dot for problem states (degraded/down). Default true. */
  pulse?: boolean
}

/**
 * The connection-health glyph: the Activity icon with a status-colored dot
 * badged in the corner (same dot-on-icon language as the offline indicator).
 * For problem states it emits a soft CSS ping ring. Shared by the top-nav
 * chip and the account-menu item so they read identically.
 */
export const HealthIndicatorIcon = ({ level, pulse = true }: Props) => {
  const color = HEALTH_COLOR[level]
  const isProblem = level === 'degraded' || level === 'down'
  const shouldPulse = pulse && isProblem

  return (
    <Box position="relative" display="inline-flex" boxSize={5}>
      <Icon as={Activity} boxSize={5} color="fg.muted" />
      <Box
        position="absolute"
        top="-1px"
        right="-1px"
        boxSize="0.5rem"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
      >
        {shouldPulse && (
          <Box
            position="absolute"
            boxSize="0.5rem"
            borderRadius="full"
            bg={color}
            css={{
              animation: `${pingKeyframes} 1.6s cubic-bezier(0,0,0.2,1) infinite`,
            }}
          />
        )}
        <Circle
          size="0.5rem"
          bg={color}
          borderWidth="2px"
          borderColor="bg.default"
        />
      </Box>
    </Box>
  )
}
